import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { Mail, User, Loader } from 'lucide-react';
import { APP_NAME } from '../constants/app';

export const AcceptInvitation: React.FC = () => {
  const { login } = useAuth();
  const { navigate } = useNavigation();
  const [token, setToken] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [invitationEmail, setInvitationEmail] = useState('');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Get token from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    let tokenParam = urlParams.get('token');
    
    // Handle SendGrid redirect URLs - check if we're on a SendGrid click tracking URL
    // SendGrid redirects usually preserve query params, but check hash as fallback
    if (!tokenParam) {
      // Check if we're on a SendGrid redirect URL (e.g., url2081.3pm.app/ls/click)
      const currentUrl = window.location.href;
      if (currentUrl.includes('/ls/click')) {
        // Try to extract token from hash or check if SendGrid preserved it in the redirect
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        tokenParam = hashParams.get('token');
        
        // If still not found, try to decode the SendGrid redirect URL
        // SendGrid encodes the destination URL in the 'upn' parameter
        const sendGridParams = new URLSearchParams(window.location.search);
        const upn = sendGridParams.get('upn');
        if (upn) {
          try {
            // The upn parameter is URL-encoded, decode it
            const decodedUpn = decodeURIComponent(upn);
            // Try to extract token from the decoded URL
            const tokenMatch = decodedUpn.match(/[?&]token=([^&]+)/);
            if (tokenMatch) {
              tokenParam = decodeURIComponent(tokenMatch[1]);
            }
          } catch (error) {
            console.error('Error decoding SendGrid redirect URL:', error);
          }
        }
      }
    }
    
    if (tokenParam) {
      setToken(tokenParam);
      // Optionally decode token to get email for display
      try {
        const payload = JSON.parse(atob(tokenParam.split('.')[1]));
        setInvitationEmail(payload.email);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    } else {
      setMessage('No invitation token found. Please check your invitation email and use the link provided.');
    }
  }, []);

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !firstName || !lastName) return;

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${apiUrl}/api/auth/accept-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, firstName, lastName }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Store token and user info
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setMessage('Invitation accepted! Redirecting...');
        
        // Call login with the token to update auth context
        await login(data.user.email);
        
        // Redirect to dashboard
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        setMessage(data.message || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Accept invitation error:', error);
      setMessage('Failed to accept invitation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="mb-6">
              <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
            </div>
            <div className="text-error-600 mb-4">
              <Mail className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 dark:text-gray-400">
              The invitation link is invalid or has expired.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mb-6">
            <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Accept Invitation</h2>
          {invitationEmail && (
            <p className="text-gray-600 dark:text-gray-400">
              You've been invited as <strong>{invitationEmail}</strong>
            </p>
          )}
        </div>

        <form onSubmit={handleAcceptInvitation} className="space-y-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                placeholder="Enter your first name"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                placeholder="Enter your last name"
                required
              />
            </div>
          </div>

          {message && (
            <div className={`p-3 rounded-md text-sm ${
              message.includes('success') || message.includes('Redirecting')
                ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
                : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
            }`}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Accepting...</span>
              </>
            ) : (
              <span>Accept Invitation</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};


